import { useEffect, useState } from "react";
import * as Y from "yjs";
import { useYDoc } from "./yjs-provider";

// ── Schema Definitions ──

export interface PropertyDef {
  key: string;
  label: string;
  type: "string" | "number" | "boolean" | "select" | "color";
  category?: "conditional";
  default?: unknown;
  options?: string[]; // for "select" type
}

export type ChatEventEntryKind = "chat" | "event" | "dice-roll";

export type DiceRollVisibility = "public" | "gm-only";

export interface DiceRollMetadata {
  diceExpression: string;
  diceResult: number;
  diceBreakdown: string;
  diceVisibility: DiceRollVisibility;
  diceRolls?: number[];
}

export interface ChatEventMetadata {
  entryKind: ChatEventEntryKind;
  createdAt?: number;
  speaker?: string;
  category?: "combat" | "movement" | "story" | "interaction";
}

export interface ChatEventMetadataDiceRoll
  extends ChatEventMetadata,
    DiceRollMetadata {
  entryKind: "dice-roll";
}

export interface ChatEventMetadataBase extends ChatEventMetadata {
  entryKind: Exclude<ChatEventEntryKind, "dice-roll">;
}

export interface TypeDef {
  id: string;
  label: string;
  icon: string; // Lucide icon name
  color: string; // Tailwind color class (e.g. "emerald", "red", "amber")
  properties: PropertyDef[];
  roles?: string[];
  viewModes?: string[];
  spatial: boolean; // Whether instances can appear on canvas
}

// ── Built-in Type Definitions ──

export const DEFAULT_TYPES: TypeDef[] = [
  {
    id: "token",
    label: "Token",
    icon: "user",
    color: "emerald",
    spatial: true,
    properties: [
      {
        key: "maxHp",
        label: "Max HP",
        type: "number",
        default: 0,
        category: "conditional",
      },
      {
        key: "hp",
        label: "HP",
        type: "number",
        default: 0,
        category: "conditional",
      },
      { key: "ac", label: "AC", type: "number", default: 10 },
      {
        key: "status",
        label: "Status",
        type: "select",
        options: ["active", "unconscious", "dead"],
        default: "active",
      },
      {
        key: "conditions",
        label: "Conditions",
        type: "string",
        default: "",
        category: "conditional",
      },
      { key: "color", label: "Color", type: "color", default: "#22c55e" },
    ],
  },
  {
    id: "npc",
    label: "NPC",
    icon: "message-circle",
    color: "sky",
    spatial: true,
    properties: [
      {
        key: "maxHp",
        label: "Max HP",
        type: "number",
        default: 0,
        category: "conditional",
      },
      {
        key: "hp",
        label: "HP",
        type: "number",
        default: 0,
        category: "conditional",
      },
      {
        key: "conditions",
        label: "Conditions",
        type: "string",
        default: "",
        category: "conditional",
      },
      { key: "role", label: "Role", type: "string", default: "" },
      { key: "faction", label: "Faction", type: "string", default: "" },
    ],
  },
  {
    id: "prop",
    label: "Prop",
    icon: "box",
    color: "amber",
    spatial: true,
    properties: [
      { key: "description", label: "Description", type: "string", default: "" },
      {
        key: "interactable",
        label: "Interactable",
        type: "boolean",
        default: true,
      },
    ],
  },
  {
    id: "shape",
    label: "Shape",
    icon: "pentagon",
    color: "stone",
    spatial: true,
    properties: [
      { key: "fill", label: "Fill", type: "color", default: "#78716c" },
      { key: "opacity", label: "Opacity", type: "number", default: 1 },
    ],
  },
  {
    id: "location",
    label: "Location",
    icon: "map-pin",
    color: "violet",
    spatial: false,
    properties: [
      { key: "description", label: "Description", type: "string", default: "" },
      { key: "region", label: "Region", type: "string", default: "" },
      {
        key: "gridType",
        label: "Grid Type",
        type: "select",
        options: ["square", "hex"],
        default: "square",
      },
      {
        key: "snapToGrid",
        label: "Snap To Grid",
        type: "boolean",
        default: false,
      },
    ],
  },
  {
    id: "encounter",
    label: "Encounter",
    icon: "swords",
    color: "red",
    spatial: false,
    roles: ["container"],
    viewModes: ["scene"],
    properties: [
      {
        key: "status",
        label: "Status",
        type: "select",
        options: ["preparing", "active", "paused", "ended"],
        default: "preparing",
      },
      { key: "round", label: "Round", type: "number", default: 0 },
      {
        key: "currentTurnIndex",
        label: "Current Turn Index",
        type: "number",
        default: 0,
      },
      {
        key: "turnOrder",
        label: "Turn Order",
        type: "string",
        default: "",
      },
    ],
  },
  {
    id: "document",
    label: "Document",
    icon: "file-text",
    color: "zinc",
    spatial: false,
    properties: [
      { key: "summary", label: "Summary", type: "string", default: "" },
    ],
  },
];

// ── Yjs Integration ──

const TYPE_REGISTRY_KEY = "typeRegistry";

/**
 * Seeds default types into the Yjs type registry if empty.
 */
export function seedTypeRegistry(doc: Y.Doc) {
  const registryMap = doc.getMap(TYPE_REGISTRY_KEY);
  if (registryMap.size > 0) return; // already seeded

  doc.transact(() => {
    for (const typeDef of DEFAULT_TYPES) {
      const typeMap = new Y.Map<unknown>();
      typeMap.set("id", typeDef.id);
      typeMap.set("label", typeDef.label);
      typeMap.set("icon", typeDef.icon);
      typeMap.set("color", typeDef.color);
      typeMap.set("spatial", typeDef.spatial);
      typeMap.set("properties", typeDef.properties);
      registryMap.set(typeDef.id, typeMap);
    }
  });
}

/**
 * Returns all registered types as a reactive array.
 */
export function useTypeRegistry(): TypeDef[] {
  const doc = useYDoc();
  const registryMap = doc.getMap(TYPE_REGISTRY_KEY);

  const readTypes = (): TypeDef[] => {
    const types: TypeDef[] = [];
    for (const [, value] of registryMap.entries()) {
      const m = value as Y.Map<unknown>;
      types.push({
        id: m.get("id") as string,
        label: m.get("label") as string,
        icon: m.get("icon") as string,
        color: m.get("color") as string,
        spatial: m.get("spatial") as boolean,
        properties: m.get("properties") as PropertyDef[],
      });
    }
    return types;
  };

  const [types, setTypes] = useState<TypeDef[]>(readTypes);

  useEffect(() => {
    const observer = () => setTypes(readTypes());
    registryMap.observeDeep(observer);
    return () => registryMap.unobserveDeep(observer);
  }, [registryMap, readTypes]);

  return types;
}

/**
 * Returns a single type definition by ID, or undefined.
 */
export function useTypeDef(typeId: string): TypeDef | undefined {
  const types = useTypeRegistry();
  return types.find((t) => t.id === typeId);
}

/**
 * Returns a lookup map of typeId → TypeDef for efficient access.
 */
export function useTypeMap(): Map<string, TypeDef> {
  const types = useTypeRegistry();
  return new Map(types.map((t) => [t.id, t]));
}
